import { useMemo, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { useI18n } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { fillTemplate } from '../../lib/fillTemplate';
import { previewBracket, confirmBracket } from '../../api/client';
import BracketTree from './BracketTree';
import GenderBadge from '../shared/GenderBadge';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import { PencilIcon, ShuffleIcon, UndoIcon, WarningIcon } from '../shared/Icons';
import { personName } from '../../utils/personName';
import { weightText } from '../../utils/weight';
import { swapSeats, teamMeetings, type DrawStep } from '../../utils/bracketDraw';
import type { Gender, BracketSeed } from '../../types';
import Notice from '../shared/Notice';
import type { Busy } from '../../hooks/useBusy';

/** One athlete of `POST /api/brackets/tournament/:id/preview`. `seed` is the
 *  draw position (#465), not a rank. */
interface BracketPreviewSeed {
  seed: number;
  registration_id: string;
  athlete_record_id: string;
  first_name: string;
  last_name: string;
  team_name?: string;
  experience_bouts?: number;
  /** #547 — the latest weigh-in weight, else the registered weight, else the
   *  athlete weight (brackets.ts, the preview SQL). NUMERIC: a string. */
  weight?: number | string | null;
  gender?: Gender;
}
export interface BracketPreview {
  age_group: string;
  weight_class: string;
  gender: Gender;
  fighter_count: number;
  /** More boxers are confirmed in the class than a bracket holds (32). The
   *  draw has the top 32 by experience. The text of the notice comes from
   *  `t.tournament.bracketTruncated` with `total_confirmed`: the `warning`
   *  sentence of the server is English and has no code (#664). */
  truncated?: boolean;
  total_confirmed?: number;
  /** Positions 1 to `byes` hold the byes. */
  byes: number;
  /** The lot: the athletes in draw-position order. */
  seeds: BracketPreviewSeed[];
  /** The shape of the IFMA draw, from the server. A swap never changes it. */
  plan: DrawStep[];
  /** The server's signature of the lot; the confirm sends it with `drawn`. */
  draw_token: string;
}

interface Props {
  preview: BracketPreview;
  tournamentId: string;
  onClose: () => void;
  /** The write committed (#664): the parent closes the modal and reads the
   *  brackets again. The read rejects when it fails. */
  onConfirm: () => Promise<unknown>;
  /** The write lock of the draw. The page shares it with the button that opens the modal. */
  draw: Busy;
  onPreviewChange: (preview: BracketPreview) => void;
}

/**
 * The bracket preview of the IFMA draw (#465). The lot gives the byes and
 * the pairs (IFMA v3.057 §16). The manager can draw again, or swap two
 * athletes by drag and drop, as in the officials planner (a PC task: drag
 * and drop does not work on a touch screen). The team mates of the draw are
 * listed with the round in which they can first meet; a meeting in the first
 * contest of one of them is a warning, never a block. The confirm sends the
 * seats and the lot, and the audit event records both.
 */
export default function BracketPreviewModal({ preview, tournamentId, onClose, onConfirm, draw, onPreviewChange }: Props) {
  const { showError } = useToast();
  // Renamed: the local confirm() of this file confirms the bracket.
  const { confirm: ask } = useConfirm();
  const afterWrite = useAfterWrite();
  const { t } = useI18n();
  const { stageLabel, fighterCount } = useLabels();
  // #552, #605 — one confirm at a time: a double tap sent two confirms.
  const confirming = useBusy();

  // The seats after the swaps, for this lot only: a new lot starts from itself.
  const [swapped, setSwapped] = useState<{ lot: BracketPreview; seats: BracketPreviewSeed[] } | null>(null);
  const seats = swapped?.lot === preview ? swapped.seats : preview.seeds;
  const [drag, setDrag] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const drawn = preview.seeds;
  const movedAt = (pos: number) => seats[pos - 1].registration_id !== drawn[pos - 1].registration_id;
  const moves = seats.filter((_, i) => movedAt(i + 1)).length;
  const byeMoved = seats.slice(0, preview.byes).some((_, i) => movedAt(i + 1));
  const meetings = useMemo(() => teamMeetings(preview.plan, seats), [preview.plan, seats]);
  const nameAt = (pos: number) => personName(seats[pos - 1].first_name, seats[pos - 1].last_name);

  function swap(a: number, b: number) {
    if (a === b) return;
    setSwapped({ lot: preview, seats: swapSeats(seats, a, b) });
  }

  async function drawAgain() {
    if (moves > 0 && !(await ask({ message: t.tournament.drawAgainConfirm, confirmLabel: t.tournament.drawAgain }))) return;
    await draw.run(async () => {
      try {
        const res = await previewBracket(tournamentId, preview.age_group, preview.weight_class, preview.gender);
        onPreviewChange(res.data as unknown as BracketPreview);
      } catch (err) { showError(err); }
    });
  }

  async function confirm() {
    await confirming.run(async () => {
      try {
        await confirmBracket(tournamentId, {
          age_group: preview.age_group,
          weight_class: preview.weight_class,
          gender: preview.gender,
          seeds: seats as unknown as BracketSeed[],
          drawn: drawn.map(s => s.registration_id),
          draw_token: preview.draw_token,
        });
        // #664 — the bracket exists: a failed read of the list is a warning (#419), not an error.
        await afterWrite({ source: 'BracketPreviewModal:confirm', success: t.tournament.bracketConfirmed, refresh: onConfirm });
      } catch (err) { showError(err); }
    });
  }

  const seatRow = (pos: number) => {
    const a = seats[pos - 1];
    const weight = weightText(a.weight);
    const early = meetings.some(m => m.firstContest && (m.a === pos || m.b === pos));
    return (
      <div key={pos} data-testid={`draw-seat-${pos}`}
        draggable
        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDrag(pos); }}
        onDragEnd={() => { setDrag(null); setDragOver(null); }}
        onDragOver={e => { if (drag !== null) { e.preventDefault(); setDragOver(pos); } }}
        onDragLeave={() => setDragOver(prev => (prev === pos ? null : prev))}
        onDrop={e => { e.preventDefault(); if (drag !== null) swap(drag, pos); setDrag(null); setDragOver(null); }}
        className={`flex items-center gap-2 text-sm px-1.5 py-1 rounded-md cursor-move select-none ${early ? 'bg-amber-50' : ''} ${dragOver === pos && drag !== pos ? 'outline-solid outline-2 outline-blue-500 -outline-offset-2' : ''} ${drag === pos ? 'opacity-40' : ''}`}>
        <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center shrink-0">{pos}</span>
        <div className="min-w-0 truncate">
          <span className="font-medium">{personName(a.first_name, a.last_name)}</span>
          {a.team_name && <span className="text-[10px] text-gray-400 ml-1.5">{a.team_name}</span>}
        </div>
        {movedAt(pos) && <span className="inline-block opacity-70" title={t.tournament.drawMovedByHand} data-testid={`draw-moved-${pos}`}><PencilIcon className="w-2.5 h-2.5" /></span>}
        {early && <WarningIcon className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
        {/* #547 — a hybrid draws after the weigh-in: the manager sees the weights. */}
        {weight && <span data-testid={`draw-weight-${pos}`} className="text-xs text-gray-500 ml-auto shrink-0">{weight}</span>}
        <span className={`text-xs text-gray-400 shrink-0 ${weight ? '' : 'ml-auto'}`}>{t.tournament.drawExperience.replace('{n}', String(a.experience_bouts ?? 0))}</span>
      </div>
    );
  };

  const n = seats.length;
  const pairs: [number, number][] = [];
  for (let p = preview.byes + 1; p < n; p += 2) pairs.push([p, p + 1]);

  return (
    // #552 — the shared panel: Confirm stays at the bottom of the dialog, and
    // the seats scroll between the header and the footer.
    <ModalBackdrop onClose={onClose} maxWidth="5xl" layout="panel" ariaLabel={t.tournament.bracketPreview}>
      <ModalHeader
        title={t.tournament.bracketPreview}
        subtitle={(
          <span className="flex flex-wrap gap-1.5 mt-1">
            <GenderBadge gender={preview.gender} division size="sm" />
            <span className="badge badge-gray text-xs">{preview.age_group}</span>
            <span className="badge badge-amber text-xs">{preview.weight_class}</span>
            <span className="badge badge-gray text-xs">{fighterCount(preview.fighter_count)}</span>
          </span>
        )}
      />
      <ModalBody>

        {preview.truncated && (
          <Notice tone="warning" size="xs" icon data-testid="bracket-truncated" className="mb-3">
            {fillTemplate(t.tournament.bracketTruncated, { total: preview.total_confirmed ?? preview.fighter_count }, t)}
          </Notice>
        )}

        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{t.tournament.drawTitle}</div>
        {/* A drag and drop does not work on a touch screen: no hint there. */}
        <p className="hidden md:block text-xs text-gray-500 mb-2">{t.tournament.drawHint}</p>
        {byeMoved && (
          <Notice tone="warning" size="xs" icon data-testid="draw-bye-moved" className="mb-2">{t.tournament.drawByeMoved}</Notice>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 mb-3">
          {preview.byes > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">{t.tournament.drawByes}</div>
              <div className="space-y-0.5">{Array.from({ length: preview.byes }, (_, i) => seatRow(i + 1))}</div>
            </div>
          )}
          <div>
            <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">{t.tournament.drawFirstSeries}</div>
            <div className="space-y-1.5">
              {pairs.map(([p, q]) => (
                <div key={p} className="border border-gray-200 rounded-lg p-0.5">{seatRow(p)}{seatRow(q)}</div>
              ))}
            </div>
          </div>
        </div>

        {meetings.length > 0 && (
          <div className="mb-3" data-testid="draw-team-mates">
            <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">{t.tournament.drawTeamMates}</div>
            <ul className="space-y-0.5 text-xs">
              {meetings.map(m => (
                <li key={`${m.a}-${m.b}`} data-first-contest={m.firstContest ? 'true' : 'false'}
                  className={`flex items-center gap-1.5 ${m.firstContest ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
                  {m.firstContest && <WarningIcon className="w-3.5 h-3.5 shrink-0" />}
                  <span>
                    {m.a}. {nameAt(m.a)} – {m.b}. {nameAt(m.b)} ({m.team}): {t.tournament.drawMeetIn.replace('{stage}', stageLabel(m.stage))}
                    {m.firstContest && ` · ${t.tournament.drawFirstContest}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t.tournament.matchups}</div>
        <div className="mb-4 overflow-x-auto">
          <BracketTree fighterCount={preview.fighter_count} plan={preview.plan}
            seats={seats.map(s => ({ seed_number: s.seed, first_name: s.first_name, last_name: s.last_name, team_name: s.team_name }))} />
        </div>

      </ModalBody>
      <ModalFooter
        secondary={<>
          <button type="button" onClick={drawAgain} disabled={draw.busy || confirming.busy} className="btn-outline btn-sm">
            <ShuffleIcon className="w-3.5 h-3.5" /> {t.tournament.drawAgain}
          </button>
          {moves > 0 && (
            <button type="button" onClick={() => setSwapped(null)} className="btn-outline btn-sm">
              <UndoIcon className="w-3.5 h-3.5" /> {t.tournament.drawUndo}
            </button>
          )}
        </>}
        onSave={confirm}
        busy={confirming.busy}
        saveLabel={t.tournament.confirmBracket}
        saveDisabled={draw.busy}
        onCancel={onClose}
      />
    </ModalBackdrop>
  );
}
